import React, { useEffect } from 'react';

/**
 * Scroll-reveal. Elements carrying `.vp-reveal` fade/rise into view the first
 * time they enter the viewport. The CSS only hides them once `html.js-reveal`
 * is set, so content is always visible when JS or IntersectionObserver is
 * unavailable. Call `useRevealOnScroll()` once near the app root.
 */
export function useRevealOnScroll() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const root = document.documentElement;
    root.classList.add('js-reveal');

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    const observeAll = () => {
      document.querySelectorAll<HTMLElement>('.vp-reveal:not(.is-visible)').forEach((el) => {
        const r = el.getBoundingClientRect();
        // Anything already on screen (or above the fold) shows immediately.
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-visible');
        else io.observe(el);
      });
    };

    observeAll();
    const mo = new MutationObserver(() => observeAll());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      root.classList.remove('js-reveal');
    };
  }, []);
}

type RevealProps = {
  as?: React.ElementType;
  delay?: 0 | 1 | 2 | 3 | 4;
  className?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
};

const Reveal = ({ as, delay = 0, className = '', children, ...rest }: RevealProps) => {
  const Tag: React.ElementType = as || 'div';
  return (
    <Tag className={`vp-reveal ${delay ? `vp-reveal-delay-${delay}` : ''} ${className}`.trim()} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
